import { Link } from "react-router-dom";
import type { TenantVerticalLiteral } from "@eduviaos/shared";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { useTerms } from "@/hooks/useTerms";
import {
  ArrowLeft,
  Bell,
  Building2,
  CalendarClock,
  BookOpen,
  ClipboardList,
  Mail,
  Palette,
  Plug,
  Smartphone,
  Sparkles,
  Users,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@eduviaos/ui-kit";

/**
 * Phase E2c — IA Settings landing page.
 *
 * The sidebar has shipped a "Settings" entry since the AppShell was
 * created in Phase D0c, but the route wasn't wired (the catch-all
 * `path="*"` silently bounced to /dashboard). Discovered during the
 * 2026-05-03 stage E2E sweep (§6.6 of the report) and judged a
 * violation of Iron Rule #2 (no UX promises without a destination).
 *
 * Why a real page instead of removing the sidebar entry: the
 * institution admin needs visibility into what's coming. The card
 * grid below names the surfaces (Branding, Members, Email templates,
 * Integrations, Appearance, Tenant info) so principals can plan
 * around the rollout instead of being surprised when items land.
 *
 * Each card is a NON-INTERACTIVE descriptor today; sub-routes will
 * land in their owning phase (e.g. /settings/branding when SA
 * delegates branding edits to IA). Until then the cards have a
 * "Coming with phase X" badge and no CTA — refusing to dead-end the
 * user into another placeholder page.
 */

interface SettingsCard {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  /** Study-centre copy when the default description is school-shaped. */
  studyCentreDescription?: string;
  status: string;
  /** When set, the card renders as a clickable link to this route. */
  href?: string;
  /** Module whose page owns this setting; the card links there when it is on. */
  moduleHome?: { module: string; href: string };
}

const SETTINGS_CARDS: SettingsCard[] = [
  {
    icon: Palette,
    title: "Branding",
    description:
      "Upload your logo and favicon and pick your primary + secondary colours. Applied instantly across your admin portal and your students' portal.",
    status: "Available now",
    href: "/settings/branding",
  },
  {
    icon: ClipboardList,
    title: "Student form",
    description:
      "See which details you collect when adding a student — the order, which ones are required, and any extra fields set up for your institution.",
    status: "Available now",
    href: "/settings/learner-form",
  },
  {
    icon: Users,
    title: "Team form",
    description:
      "See which details you collect when inviting a staff member — the order, which ones are required, and any extra fields set up for your institution.",
    status: "Available now",
    href: "/settings/team-form",
  },
  {
    icon: Building2,
    title: "Institution info",
    description:
      "Display name, primary contact, billing address, GST registration, and operational time zone. Edits land via support today; self-serve editing lands soon.",
    status: "Coming soon",
  },
  {
    icon: Users,
    title: "Members & roles",
    description:
      'Author institution-defined designations (e.g. "Senior Counsellor") that anchor staff to a base role with extra permission overrides. Pair this with the People → Staff tab to assign designations to invited members.',
    studyCentreDescription:
      "Author designations that anchor team members to a base role with extra permission overrides. Assign them from Learners → Team.",
    status: "Available now",
    href: "/settings/designations",
  },
  {
    icon: Bell,
    title: "Notification triggers",
    description:
      "Map admission events (e.g. an enquiry advancing to 'application submitted') to a notification template + recipient list. Triggers fire asynchronously across email and in-app channels — SMS and webhook channels land in a follow-up release.",
    status: "Available now",
    href: "/settings/notifications",
    moduleHome: { module: "communications", href: "/communications" },
  },
  {
    icon: CalendarClock,
    title: "Admissions lifecycle",
    description:
      "Open or close the public apply portal, run scheduled admission windows (e.g. June–September only), restrict per grade, and customise the closed-state message parents see outside an open window. Email-OTP verification toggle lives here too.",
    studyCentreDescription:
      "Open or close the public apply portal and run admission windows. Learners see your closed-state message outside an open window.",
    status: "Available now",
    href: "/settings/admissions-lifecycle",
  },
  {
    icon: Smartphone,
    title: "Learner app",
    description:
      "Set how your centre prices the learner app (bundled with the seat or a separate monthly fee) and understand when to grant access from the roster.",
    status: "Available now",
    href: "/settings/learner-app",
  },
  {
    icon: BookOpen,
    title: "Learning defaults",
    description:
      "Cap quiz length institution-wide, set default question counts for diagnostics and practice, and choose the default AI content language (English or Hindi).",
    status: "Available now",
    href: "/settings/learning-defaults",
  },
  {
    icon: Mail,
    title: "Email templates",
    description:
      "Override the platform-default copy for outgoing emails (admissions decisions, fee receipts, parent broadcasts). Managed by the platform team today; self-serve editing lands soon.",
    status: "Coming soon",
  },
  {
    icon: Plug,
    title: "Integrations",
    description:
      "Single sign-on (Google Workspace / Microsoft Entra), payment gateways, SMS / WhatsApp providers, and webhooks for downstream systems.",
    status: "Coming soon",
  },
  {
    icon: Sparkles,
    title: "Appearance",
    description:
      "Theme palette, layout density, and motion preferences. Live today — open the Customize Appearance panel from the topbar.",
    status: "Available now",
  },
];

function visibleSettingsCards(vertical: TenantVerticalLiteral | undefined): SettingsCard[] {
  return SETTINGS_CARDS.filter((card) => {
    if (vertical === "study_centre" && card.status === "Coming soon") return false;
    return true;
  });
}

export function SettingsPage() {
  const { vertical, isModuleEnabled } = useEnabledModules();
  const { ct } = useTerms(vertical);
  const cards = visibleSettingsCards(vertical);

  return (
    <div className="mx-auto max-w-5xl py-10">
      <Button variant="ghost" size="sm" asChild className="mb-6">
        <Link to="/dashboard">
          <ArrowLeft aria-hidden="true" className="size-4" />
          Back to dashboard
        </Link>
      </Button>

      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl text-sm leading-relaxed">
          {vertical === "study_centre"
            ? "Branding, your team, the learner app, and learning defaults — the settings this centre can use today."
            : 'Configure your institution\'s branding, members, integrations, and platform behaviour. Surfaces below land progressively. Anything that says "Available now" is already wired.'}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {cards.map((card) => {
          const Icon = card.icon;
          const isLive = card.status === "Available now";
          const description =
            vertical === "study_centre" && card.studyCentreDescription
              ? card.studyCentreDescription
              : ct(card.description);
          const title = ct(card.title);
          // Cards with `href` are interactive — render the whole card as
          // a Link so the entire surface is the click target (per Iron
          // Rule #16 we don't add row-click handlers on tables, but
          // settings cards are big tile affordances and benefit from
          // a single decisive hit area).
          const inner = (
            <>
              <CardHeader className="pb-3">
                <div className="flex items-start gap-3">
                  <span
                    aria-hidden="true"
                    className="bg-primary/10 text-primary grid size-10 shrink-0 place-items-center rounded-lg"
                  >
                    <Icon className="size-5" />
                  </span>
                  <div className="min-w-0">
                    <CardTitle className="text-base font-semibold">{title}</CardTitle>
                    <CardDescription
                      className={
                        "mt-1 inline-flex items-center gap-1 text-xs " +
                        (isLive
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-muted-foreground")
                      }
                    >
                      <Sparkles aria-hidden="true" className="size-3" />
                      {card.status}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80 text-sm leading-relaxed">{description}</p>
              </CardContent>
            </>
          );

          const href =
            card.moduleHome && isModuleEnabled(card.moduleHome.module)
              ? card.moduleHome.href
              : card.href;
          if (href) {
            return (
              <Link
                key={card.title}
                to={href}
                className="focus-visible:ring-ring group rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                data-testid={`settings-card-${(card.href ?? href).replace(/\//g, "-")}`}
              >
                <Card className="border-border/80 bg-card/60 hover:border-primary/50 group-hover:bg-card/90 h-full backdrop-blur transition-colors">
                  {inner}
                </Card>
              </Link>
            );
          }

          return (
            <Card key={card.title} className="border-border/80 bg-card/60 backdrop-blur">
              {inner}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
